import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FiArrowLeft, FiCalendar, FiUser, FiEye } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import SEO from "../components/SEO";
import Loader from "../components/Loader";
import api, { imgUrl } from "../utils/api";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { SAURABH, GAURI } from "../utils/siteData";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/blogs/${slug}`)
      .then((res) => setBlog(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Loader label="Loading article..." />;

  if (notFound || !blog) {
    return (
      <div className="text-center py-24">
        <p className="text-2xl font-heading text-maroon-800 mb-4">Article Not Found</p>
        <Link to="/blog" className="text-saffron-600 font-semibold">← Back to Blog</Link>
      </div>
    );
  }

  const person = blog.author === "Gauri Shastri" ? GAURI : SAURABH;
  const waLink = buildWhatsAppLink(person.whatsapp, `Namaste 🙏, I read your article "${blog.title}" and would like to know more.`);
  const formattedDate = new Date(blog.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="py-12 bg-white">
      <SEO
        title={blog.metaTitle || blog.title}
        description={blog.metaDescription || blog.excerpt}
        path={`/blog/${blog.slug}`}
      />

      <div className="max-w-3xl mx-auto px-4">
        <Link to="/blog" className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all">
          <FiArrowLeft /> Back to Blog
        </Link>

        <span className="text-xs text-saffron-600 font-semibold uppercase tracking-wide">{blog.category}</span>
        <h1 className="font-heading text-3xl sm:text-4xl text-maroon-800 mt-2 mb-4 leading-tight">{blog.title}</h1>

        <div className="flex items-center gap-5 text-sm text-gray-500 mb-8 flex-wrap">
          <span className="flex items-center gap-1"><FiUser /> {blog.author}</span>
          <span className="flex items-center gap-1"><FiCalendar /> {formattedDate}</span>
          <span className="flex items-center gap-1"><FiEye /> {blog.views} views</span>
        </div>

        {blog.coverImage && (
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 border border-gold-100">
            <img src={imgUrl(blog.coverImage)} alt={blog.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="prose-blog text-gray-800" dangerouslySetInnerHTML={{ __html: blog.content }} />

        {blog.tags && blog.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-gold-100">
            {blog.tags.map((tag) => (
              <span key={tag} className="bg-gold-50 text-maroon-700 text-xs px-3 py-1.5 rounded-full border border-gold-200">
                #{tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-10 bg-saffron-50 rounded-2xl p-6 text-center border border-gold-100">
          <p className="text-gray-700 mb-4">Have questions about this topic? Reach out to {blog.author} directly.</p>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-full font-semibold transition-colors"
          >
            <FaWhatsapp /> Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
